import { getEpoch, getTodayWithTime } from "./DateFormatter";

interface DistrictWindow {
    key: number | string;
    startDate: string;
    endDate: string;
}

interface ActionControl {
    action: string;
    differentiator: number | string;
    startDate: string;
    endDate: string;
}

/**
 * Do the start and end dates encompass now?
 */
export const isWindowOpen = (start: string, end: string): boolean => {
    const epochNow = getEpoch(getTodayWithTime());
    if (start && end && epochNow !== null) {
        return (getEpoch(start) ?? 0) <= epochNow && (getEpoch(end) ?? 0) > epochNow;
    }
    return false;
};

/**
 * Is the districtWindow open?
 */
export const isRoundOpen = (districtWindow: DistrictWindow | undefined): boolean => {
    if (districtWindow) {
        return isWindowOpen(districtWindow.startDate, districtWindow.endDate);
    }
    return false;
};

/**
 * Return the requested action control object.
 */
export const getActionControl = (
    action: string,
    actionControls: ActionControl[],
    districtWindow: DistrictWindow
): ActionControl | null => {
    const result = actionControls.filter(
        (obj) =>
            obj.action === action &&
            Number.parseInt(String(obj.differentiator), 10) === Number.parseInt(String(districtWindow.key), 10)
    );
    if (result && result.length > 0) {
        return result[0];
    }
    return null;
};

/**
 * Is the specific action control open?
 */
export const isAllowed = (
    action: string,
    actionControls: ActionControl[],
    activeDistrictWindow: DistrictWindow,
    districtWindows: DistrictWindow[]
): boolean => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const actionControl = getActionControl(action, actionControls, activeDistrictWindow);
    if (actionControl && isWindowOpen(actionControl.startDate, actionControl.endDate)) {
        return true;
    }
    return false;
};

/**
 * Is the PARENT_REQUEST window open for this round?
 */
export const parentRequestAllowed = (
    actionControls: ActionControl[],
    activeDistrictWindow: DistrictWindow,
    districtWindows: DistrictWindow[]
): boolean => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const parentRequest = getActionControl("PARENT_REQUEST", actionControls, activeDistrictWindow);
    if (parentRequest && isWindowOpen(parentRequest.startDate, parentRequest.endDate)) {
        return true;
    }
    return false;
};

/**
 * Are we between PARENT_REQUEST windows?
 */
export const parentRequestBetweenRounds = (
    actionControls: ActionControl[],
    districtWindows: DistrictWindow[]
): boolean => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const parentRequest = getActionControl("PARENT_REQUEST", actionControls, districtWindows[0]);
    const parentRequestRoundTwo = getActionControl("PARENT_REQUEST", actionControls, districtWindows[1]);
    if (
        parentRequest &&
        parentRequestRoundTwo &&
        isWindowOpen(parentRequest.endDate, parentRequestRoundTwo.startDate)
    ) {
        return true;
    }
    return false;
};

/**
 * Is current time between finalize round1 and parent request round2?
 */
export const parentViewBetweenRounds = (
    actionControls: ActionControl[],
    districtWindows: DistrictWindow[]
): boolean => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const schoolFinalizeRoundOne = getActionControl("SCHOOL_FINALIZE", actionControls, districtWindows[0]);
    const parentRequestRoundTwo = getActionControl("PARENT_REQUEST", actionControls, districtWindows[1]);
    if (
        schoolFinalizeRoundOne &&
        parentRequestRoundTwo &&
        isWindowOpen(schoolFinalizeRoundOne.startDate, parentRequestRoundTwo.startDate)
    ) {
        return true;
    }
    return false;
};

/**
 * Is the SCHOOL_VIEW_PARENT_OFFER window open?
 */
export const schoolViewParentOfferAllowed = (
    actionControls: ActionControl[],
    activeDistrictWindow: DistrictWindow,
    districtWindows: DistrictWindow[]
): boolean => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const schoolViewParentOffer = getActionControl("SCHOOL_VIEW_PARENT_OFFER", actionControls, activeDistrictWindow);
    if (schoolViewParentOffer && isWindowOpen(schoolViewParentOffer.startDate, schoolViewParentOffer.endDate)) {
        return true;
    }
    return false;
};

/**
 * Is the SCHOOL_VIEW_ONLY window open?
 */
export const schoolViewOnlyAllowed = (
    actionControls: ActionControl[],
    activeDistrictWindow: DistrictWindow,
    districtWindows: DistrictWindow[]
): boolean => {
    if (!isRoundOpen(districtWindows[0]) && !isRoundOpen(districtWindows[1])) {
        return false;
    }
    const schoolViewOnly = getActionControl("SCHOOL_VIEW_ONLY", actionControls, activeDistrictWindow);
    if (schoolViewOnly && isWindowOpen(schoolViewOnly.startDate, schoolViewOnly.endDate)) {
        return true;
    }
    return false;
};
